export function digitsOnly(value) {
  return String(value ?? "").replace(/\D/g, "");
}

/**
 * Canonical international mobile format for storage/display: "+<code> <digits>"
 * - Ensures exactly one leading "+"
 * - Ensures exactly one space between code and number when number exists
 * - Strips all non-digits from the national number
 */
export function formatInternationalMobile({ phoneCode, number }) {
  const code = digitsOnly(phoneCode);
  const national = digitsOnly(number);

  if (!code && !national) return "";
  if (code && !national) return `+${code}`;
  if (!code && national) return national; // best-effort fallback (shouldn't happen for normal UI flows)

  return `+${code} ${national}`;
}

/**
 * Tolerant parser for legacy `iMobile` payloads saved as JSON.
 * Accepts:
 * - json string or object
 * - mobileNumber formats: "374 786...", "+374 786...", " +374 786...", "+374786..."
 */
export function parseIMobile(iMobile) {
  let obj = {};
  try {
    obj = typeof iMobile === "string" ? JSON.parse(iMobile) : (iMobile || {});
  } catch {
    obj = {};
  }

  const countryTitle = String(obj?.countryTitle ?? "");
  const countryFlagClass = String(obj?.countryFlagClass ?? "");

  const rawMobileNumber = String(obj?.mobileNumber ?? "");
  const raw = rawMobileNumber.trim();

  // Try to extract: optional +, code (1-4 digits), then the rest as national number.
  // We keep this permissive to support existing rows in DB.
  const m = raw.match(/^\+?(\d{1,4})\s*(.*)$/);
  const phoneCode = m ? digitsOnly(m[1]) : "";
  const nationalNumber = m ? digitsOnly(m[2]) : digitsOnly(raw);

  // Prefer code derived from mobileNumber, but attempt to infer from "+NN" like strings with no spaces.
  const formatted = formatInternationalMobile({ phoneCode, number: nationalNumber });

  return {
    countryTitle,
    countryFlagClass,
    rawMobileNumber,
    phoneCode: phoneCode || "",
    nationalNumber: nationalNumber || "",
    formattedMobileNumber: formatted,
  };
}

